import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2Off, Search } from "lucide-react";
import { useEffect, useState } from "react";

import FileDropZone from "@/components/common/FileDropZone";
import {
  createQuotationItemDrawing,
  thumbnailSrc,
  uploadDrawingRevision,
} from "@/features/drawings/api";
import type { PanelDrawing } from "@/features/drawings/panel/types";
import { errorMessage } from "@/lib/api";
import { showError, showSuccess } from "@/lib/toast";
import { linkQuotationDrawing, type QuotationItem } from "./api";
import { quotationDrawingsQuery } from "./drawingsQuery";

/**
 * 明細の側から図面を選んで紐付けるダイアログ。**明細一覧の「図面リンク」から開く**。
 *
 * 紐付けは図面タブからもできる（[[DrawingDetailDialog]]）が、あちらは図面 1 枚を
 * 開いて明細を選ぶ形で、**向きが逆**。明細を上から順に片付けているときに「この行
 * だけ図面が付いていない」と気づくことが多く、そのたびに図面タブへ移って該当の
 * 図面を探すのは遠い。ここは行から入って図面を選ぶ。
 *
 * 候補に出すのは**まだどの行にも付いていない図面**だけ。1 つの明細に 2 枚は付かず、
 * 既に別の行が使っている図面を選ばせると、その行から黙って図面が外れる
 * （図面タブの候補の絞り方と対になっている）。
 *
 * 図番が明細と一致する図面を先頭に寄せ、印を付ける。突き合わせで決まらなかった
 * 図面（図番が読めていない・表記が揺れている）を手で付けるのがこの画面の仕事
 * なので、**機械が決められなかった中でも「たぶんこれ」が分かる**ようにしておく。
 *
 * **ここから PDF を直接登録もできる。** 客先の図面が揃っていない行は、候補の中に
 * そもそも相手がいない。図面タブへ移って取り込み、戻ってきて選ぶ手間を省く。
 * 登録した図面の図番は**明細の図番をそのまま入れる**ので、図番の読み取り（OCR）は
 * 行わない（読んでも採用されない。[[uploadDrawingRevision]]）。絵（サムネイル）は
 * 一覧でどの図面かを見分ける手がかりなので作る。
 */
export default function ItemDrawingLinkDialog({
  quotationId,
  item,
  canWrite,
  onClose,
}: {
  quotationId: number;
  /** null のとき閉じている。 */
  item: QuotationItem | null;
  canWrite: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState("");

  // 開き直したら絞り込みは白紙に戻す（前の行で打った図番が残っていると、
  // 候補が 0 件に見えて「図面が無い」と読み違える）。
  useEffect(() => {
    if (item) setKeyword("");
  }, [item]);

  const drawingsQuery = useQuery({
    ...quotationDrawingsQuery(quotationId),
    enabled: item != null,
  });
  const drawings = drawingsQuery.data?.items ?? [];

  /** いまこの行に付いている図面。 */
  const linked = item
    ? (drawings.find((d) => d.item_id === item.quotation_item_id) ?? null)
    : null;

  /** 図面を変えると明細の図面リンク・版数の表示も変わる。 */
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: quotationDrawingsQuery(quotationId).queryKey,
      }),
      queryClient.invalidateQueries({ queryKey: ["quotation-items", quotationId] }),
    ]);

  /**
   * 紐付けを張り替える。
   *
   * 付け替えのときは**先に今の図面を外してから**付ける。API は図面 1 枚ずつの
   * 操作なので、外さずに付けると 2 枚が同じ行を指し、どちらがその行の図面なのか
   * が決まらなくなる。
   */
  const relink = useMutation({
    mutationFn: async (drawing: PanelDrawing | null) => {
      if (item == null) return;
      if (linked && linked.drawing_id !== drawing?.drawing_id) {
        await linkQuotationDrawing(quotationId, linked.drawing_id, null);
      }
      if (drawing) {
        await linkQuotationDrawing(
          quotationId,
          drawing.drawing_id,
          item.quotation_item_id,
        );
      }
    },
    onSuccess: async (_data, drawing) => {
      await invalidate();
      showSuccess(
        drawing
          ? `${drawing.drawing_number ?? "図番なしの図面"} を ${item?.sequence} 行目に紐付けました。`
          : "紐付けを外しました。",
      );
      onClose();
    },
    onError: (e: Error) => showError(e.message),
  });

  /** どの行にも付いていない図面。これが 0 件なら選びようがない。 */
  const unlinked = drawings.filter((d) => d.item_id == null);

  /**
   * 候補。**どの行にも付いていない図面**だけを、図番一致 → 図番順に並べる。
   */
  const candidates = unlinked
    .filter((d) => {
      const word = keyword.trim().toLowerCase();
      if (!word) return true;
      return (d.drawing_number ?? "").toLowerCase().includes(word);
    })
    .sort((a, b) => {
      const matched = (d: PanelDrawing) =>
        item?.drawing_number != null && d.drawing_number === item.drawing_number;
      if (matched(a) !== matched(b)) return matched(a) ? -1 : 1;
      return (a.drawing_number ?? "").localeCompare(b.drawing_number ?? "");
    });

  /**
   * PDF を 1 枚登録して、この行に添える。
   *
   * 明細を足しただけでは図面の器は作られない（中身の無い図面行が増えるだけ）
   * ので、器を作ってから PDF を載せる。器を作った時点で**図番は明細のものが
   * 入り、紐付けも済んでいる**（[[create_quotation_item_drawing]]）。
   */
  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (item == null) return null;
      const drawing = await createQuotationItemDrawing(
        quotationId,
        item.quotation_item_id,
      );
      // 図番は決まっているので読み取りは頼まない。絵は作られる。
      await uploadDrawingRevision(drawing.drawing_id, file, { readNumber: false });
      return drawing;
    },
    onSuccess: async () => {
      await invalidate();
      showSuccess(`${item?.drawing_number} の図面を登録して紐付けました。`);
      onClose();
    },
    onError: (e: Error) =>
      showError(`PDF を登録できませんでした: ${errorMessage(e, "原因不明のエラー")}`),
  });

  const busy = relink.isPending || upload.isPending;

  return (
    <Modal
      isOpen={item != null}
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>図面の紐付け</span>
          <span className="text-small font-normal text-default-500">
            {item?.sequence} 行目 {item?.drawing_number} {item?.item_name}
          </span>
        </ModalHeader>

        <ModalBody>
          {/* いま付いている図面。外す操作はここに置く（候補の中に混ぜると、
              付けるのと外すのが同じ並びに見える）。 */}
          {linked && (
            <div className="flex items-center gap-3 rounded-md border border-brand-200 bg-brand-50 p-3">
              <DrawingThumbnail drawing={linked} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold">
                  {linked.drawing_number ?? "（図番なし）"}
                </p>
                <p className="text-tiny text-default-500">
                  いま紐付いている図面（版 {linked.revision_count}）
                </p>
              </div>
              <Button
                size="sm"
                radius="full"
                variant="flat"
                color="danger"
                startContent={<Link2Off size={16} />}
                isDisabled={!canWrite || busy}
                data-testid="unlink-drawing"
                onPress={() => relink.mutate(null)}
              >
                紐付けを外す
              </Button>
            </div>
          )}

          {/* 候補が 1 件も無いときは出さない。打つ相手がいない欄が残ると、
              絞り込んだせいで消えたのかどうかが分からない。 */}
          {unlinked.length > 0 && (
            <Input
              size="sm"
              variant="bordered"
              radius="sm"
              aria-label="図番で絞り込み"
              placeholder="図番で絞り込み"
              value={keyword}
              isClearable
              startContent={<Search size={16} className="text-default-400" />}
              onValueChange={setKeyword}
              onClear={() => setKeyword("")}
            />
          )}

          {drawingsQuery.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner label="読み込み中..." />
            </div>
          ) : candidates.length === 0 ? (
            <p className="py-2 text-sm text-default-500">
              {keyword.trim()
                ? "その図番の図面は見つかりません。"
                : "紐付けできる図面がまだありません。下から PDF を登録するか、図面タブで取り込んでください。"}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-default-100">
              {candidates.map((drawing) => {
                const matched =
                  item?.drawing_number != null &&
                  drawing.drawing_number === item.drawing_number;
                return (
                  <li
                    key={drawing.drawing_id}
                    className="flex items-center gap-3 py-2 text-sm"
                  >
                    <DrawingThumbnail drawing={drawing} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-bold">
                        {drawing.drawing_number ?? "（図番なし）"}
                      </p>
                      <p className="text-tiny text-default-400">
                        版 {drawing.revision_count}
                        {drawing.origin_note && ` / ${drawing.origin_note}`}
                      </p>
                    </div>
                    {/* 図番が一致する図面は、まず間違いなくこれ。並び順だけだと
                        「上にあるから」なのか「一致したから」なのかが分からない。 */}
                    {matched && (
                      <Chip size="sm" variant="flat" color="success">
                        図番一致
                      </Chip>
                    )}
                    <Button
                      size="sm"
                      radius="full"
                      color="primary"
                      variant={matched ? "solid" : "bordered"}
                      // 同じ文言のボタンが並ぶので、読み上げにはどの図面かを乗せる。
                      aria-label={`${drawing.drawing_number ?? "図番なしの図面"} を紐付ける`}
                      isDisabled={!canWrite || busy}
                      onPress={() => relink.mutate(drawing)}
                    >
                      紐付ける
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* 候補の中に相手がいない行のための口。客先の図面が 1 枚ずつ遅れて
              届くことがあり、そのたびに図面タブへ移って取り込み、戻ってきて
              選ぶのは遠い。**既に図面が付いている行には出さない**（差し替えは
              版を足す操作で、見積入力画面の「図面を更新」が受け持つ）。 */}
          {canWrite && !linked && (
            <div className="flex flex-col gap-2 border-t border-default-100 pt-3">
              <p className="text-sm font-bold">この行の図面を登録する</p>
              <FileDropZone
                label="図面PDF"
                accept=".pdf"
                showLabel={false}
                hint={`図番は明細の ${item?.drawing_number} が入ります（図面からの読み取りはしません）。`}
                // 選んだ時点で登録する。ここでの PDF は 1 枚なので、選んでから
                // もう一度ボタンを押させる意味が無い（図面タブと同じ作り）。
                file={null}
                isDisabled={busy}
                onSelect={(file) => file && upload.mutate(file)}
              />
              {upload.isPending && (
                <p className="text-tiny text-default-500">登録しています...</p>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button radius="full" variant="light" data-testid="close" onPress={onClose}>
            閉じる
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** 一覧のカードと同じ絵を小さく出す。どの図面かは図番より絵のほうが早い。 */
function DrawingThumbnail({ drawing }: { drawing: PanelDrawing }) {
  const src = thumbnailSrc(drawing.thumbnail_path);
  return (
    <div className="flex h-12 w-16 shrink-0 items-center justify-center overflow-hidden rounded border border-default-200 bg-default-50">
      {src ? (
        <img src={src} alt="" className="size-full object-contain" />
      ) : (
        <span className="text-tiny text-default-400">絵なし</span>
      )}
    </div>
  );
}
